import type { Correspondence, Planet, SigilNode } from "../types";

export interface ReductionRules {
  removeVowels: boolean;
  removeDuplicates: boolean;
  keepY: boolean;
}

export const DEFAULT_RULES: ReductionRules = {
  removeVowels: true,
  removeDuplicates: true,
  keepY: true,
};

export function reduceIntent(value: string, rules: ReductionRules = DEFAULT_RULES): string {
  let letters = value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");
  if (rules.removeVowels) letters = letters.replace(rules.keepY ? /[AEIOU]/g : /[AEIOUY]/g, "");
  if (rules.removeDuplicates) {
    const seen = new Set<string>();
    letters = [...letters].filter((letter) => {
      if (seen.has(letter)) return false;
      seen.add(letter);
      return true;
    }).join("");
  }
  return letters;
}

export function localCorrespondence(intent: string): Correspondence {
  const lower = intent.toLowerCase();
  const groups: Array<Correspondence & { words: string[] }> = [
    { planet: "saturn", element: "earth", palette: "negra", rationale: "Saturn and earth support boundaries, discipline, endings, and durable protection.", words: ["protect", "boundary", "discipline", "banish", "end"] },
    { planet: "jupiter", element: "air", palette: "dorada", rationale: "Jupiter and air emphasize expansion, prosperity, learning, and wise opportunity.", words: ["prosper", "grow", "wealth", "wisdom", "opportunity"] },
    { planet: "mars", element: "fire", palette: "roja", rationale: "Mars and fire lend directness, stamina, courage, and decisive motion.", words: ["courage", "victory", "strength", "drive", "fight"] },
    { planet: "sun", element: "fire", palette: "dorada", rationale: "The Sun and fire emphasize clarity, vitality, recognition, and centered confidence.", words: ["success", "health", "confidence", "clarity", "recognition"] },
    { planet: "venus", element: "water", palette: "roja", rationale: "Venus and water speak to affection, beauty, pleasure, and reciprocal harmony.", words: ["love", "beauty", "friend", "harmony", "pleasure"] },
    { planet: "mercury", element: "air", palette: "morada", rationale: "Mercury and air govern language, technique, data, movement, and quick understanding.", words: ["learn", "write", "speak", "data", "travel", "skill"] },
    { planet: "moon", element: "water", palette: "blanca", rationale: "The Moon and water favor dreams, memory, intuition, home, and reflective healing.", words: ["dream", "intuition", "home", "heal", "memory"] },
  ];
  const ranked = groups
    .map((group) => ({ group, score: group.words.filter((word) => lower.includes(word)).length }))
    .sort((a, b) => b.score - a.score);
  const selected = ranked[0].score ? ranked[0].group : groups[lower.length % groups.length];
  const { words: _words, ...result } = selected;
  return result;
}

export function generateWheelNodes(letters: string, planet: Planet = "wheel"): SigilNode[] {
  const offsets: Record<Planet, number> = {
    wheel: 0, saturn: 0.2, jupiter: 0.48, mars: 0.76, sun: 1.04, venus: 1.32, mercury: 1.6, moon: 1.88,
  };
  return [...(letters || "SGL")].map((letter, index) => {
    const alphabet = letter.charCodeAt(0) - 65;
    const angle = alphabet / 26 * Math.PI * 2 - Math.PI / 2 + offsets[planet];
    const radius = 0.29 + ((alphabet + index) % 3) * 0.095;
    return { letter, x: 0.5 + Math.cos(angle) * radius, y: 0.5 + Math.sin(angle) * radius };
  });
}

export function createSigilSvg(
  nodes: SigilNode[],
  options: { stroke: number; glow: number; symmetry: number; ink: string; binding: string; mode: string; tension: number },
): string {
  const size = 640;
  const colors: Record<string, string> = { neon: "#8ff8ff", chalk: "#eee8d8", blood: "#8f1f29", gold: "#d4af62" };
  const color = colors[options.ink] || colors.gold;
  const points = nodes.map((node) => `${(node.x * size).toFixed(1)},${(node.y * size).toFixed(1)}`);
  const straight = `M ${points.join(" L ")}`;
  const transforms = Array.from({ length: options.symmetry }, (_, index) => {
    const angle = index * 360 / options.symmetry;
    const mirror = index % 2 ? " scale(-1 1)" : "";
    return `<path d="${straight}" transform="rotate(${angle} 320 320) translate(${index % 2 ? 640 : 0} 0)${mirror}"/>`;
  }).join("");
  const binding = options.binding === "none" ? "" : `<circle cx="320" cy="320" r="274" fill="none" stroke="${color}" stroke-width="${Math.max(1, options.stroke * .45)}" opacity=".72"/>`;
  const ticks = options.binding === "runic" || options.binding === "celestial"
    ? Array.from({ length: options.binding === "runic" ? 24 : 12 }, (_, index) => `<path d="M 320 31 L 320 ${index % 3 ? 43 : 53}" transform="rotate(${index * (options.binding === "runic" ? 15 : 30)} 320 320)"/>`).join("")
    : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><defs><filter id="glow"><feGaussianBlur stdDeviation="${(options.glow / 3).toFixed(2)}" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs><g fill="none" stroke="${color}" stroke-linecap="round" stroke-linejoin="round" stroke-width="${options.stroke}" filter="url(#glow)">${binding}${ticks}${transforms}</g></svg>`;
}

export function downloadText(filename: string, contents: string, type: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
